import client from '../client';

export interface Platform {
  name: string;
  connected: boolean;
  last_synced?: string;
  credentials?: {
    valid: boolean;
    expires_at?: string;
  };
  // UI properties
  icon?: React.ReactNode;
  color?: string;
}

export interface SocialPost {
  id: number;
  content: string;
  platforms: string[];
  media_urls?: string[];
  scheduled_for?: string;
  status: 'draft' | 'scheduled' | 'published' | 'failed';
  engagement?: {
    likes: number;
    comments: number;
    shares: number;
  };
  business_id: number;
  created_at: string;
  updated_at: string;
}

export interface PostSchedule {
  id: number;
  post_id: number;
  scheduled_time: string;
  platforms: string[];
  status: 'pending' | 'completed' | 'failed';
}

const socialService = {
  // Posts
  createPost: async (post: {
    business_id: number;
    content: string;
    platforms: string[];
    media_urls?: string[];
    scheduled_for?: string;
  }): Promise<SocialPost> => {
    const response = await client.post<SocialPost>('/social/posts', post);
    return response.data;
  },

  getPosts: async (
    businessId: number,
    params?: {
      status?: string;
      platform?: string;
      start_date?: string;
      end_date?: string;
    }
  ): Promise<SocialPost[]> => {
    const response = await client.get<SocialPost[]>(`/social/posts/${businessId}`, { params });
    return response.data;
  },

  updatePost: async (
    postId: number,
    updates: Partial<SocialPost>
  ): Promise<SocialPost> => {
    const response = await client.patch<SocialPost>(`/social/posts/${postId}`, updates);
    return response.data;
  },

  deletePost: async (postId: number): Promise<void> => {
    await client.delete(`/social/posts/${postId}`);
  },

  // Platforms
  getPlatforms: async (businessId: number): Promise<Platform[]> => {
    const response = await client.get<Platform[]>(`/social/platforms/${businessId}`);
    return response.data;
  },

  connectPlatform: async (
    businessId: number,
    platform: string,
    credentials: Record<string, any>
  ): Promise<Platform> => {
    const response = await client.post<Platform>(`/social/platforms/${businessId}/connect`, {
      platform,
      credentials,
    });
    return response.data;
  },

  disconnectPlatform: async (
    businessId: number,
    platform: string
  ): Promise<void> => {
    await client.post(`/social/platforms/${businessId}/disconnect`, { platform });
  },

  // Scheduling
  getSchedule: async (
    businessId: number,
    params?: {
      start_date?: string;
      end_date?: string;
      platform?: string;
    }
  ): Promise<PostSchedule[]> => {
    const response = await client.get<PostSchedule[]>(`/social/schedule/${businessId}`, { params });
    return response.data;
  },

  schedulePost: async (
    postId: number,
    schedule: {
      scheduled_time: string;
      platforms: string[];
    }
  ): Promise<PostSchedule> => {
    const response = await client.post<PostSchedule>(`/social/posts/${postId}/schedule`, schedule);
    return response.data;
  },

  cancelSchedule: async (scheduleId: number): Promise<void> => {
    await client.delete(`/social/schedule/${scheduleId}`);
  },

  // Content Generation
  generateContent: async (
    businessId: number,
    params: {
      topic?: string;
      type?: 'post' | 'story' | 'article';
      tone?: 'professional' | 'casual' | 'friendly';
      platforms?: string[];
      keywords?: string[];
    }
  ): Promise<{ content: string; suggestions: string[] }> => {
    const response = await client.post<{ content: string; suggestions: string[] }>(
      `/social/generate/${businessId}`,
      params
    );
    return response.data;
  },

  // Analytics
  getEngagement: async (
    businessId: number,
    params: {
      start_date: string;
      end_date: string;
      platform?: string;
      metric?: 'likes' | 'comments' | 'shares' | 'all';
    }
  ): Promise<{
    total: number;
    by_platform: Record<string, number>;
    by_date: Record<string, number>;
  }> => {
    const response = await client.get<{
      total: number;
      by_platform: Record<string, number>;
      by_date: Record<string, number>;
    }>(`/social/engagement/${businessId}`, { params });
    return response.data;
  },

  getBestTimes: async (
    businessId: number,
    platform: string
  ): Promise<{
    days: Record<string, string[]>;
    overall_best: string[];
  }> => {
    const response = await client.get<{
      days: Record<string, string[]>;
      overall_best: string[];
    }>(`/social/best-times/${businessId}?platform=${platform}`);
    return response.data;
  },
};

export default socialService;
